/* global Word, Office */
import type { TemplateProfile } from '../templates';
import type { ElementDefinition } from './types';
import { AppError, AppErrorCode } from '../core/errors';

const CALLOUT_WIDTH_PT = 120;
const CALLOUT_INIT_HEIGHT_PT = 20;
const DUMMY_TEXT = 'Enter callout text here.';
const CALLOUT_TEXT_COLOR = '#767676'; // light grey, matches previous design
const WRAP_GAP_PT = 8; // gap between the box and the wrapped body text
const PAGE_LEFT_GAP_PT = 18; // space between the page's left edge and a margin callout (~0.6 cm)

// Shape names identify which exhibit table a callout belongs to, so a second
// callout is not added for the same exhibit.
const CALLOUT_NAME_NEXT_TABLE = 'SBIMF Callout (next table)'; // anchored just before its table
const CALLOUT_NAME_PREV_TABLE = 'SBIMF Callout (previous table)'; // anchored just after its table
const CALLOUT_NAME = 'SBIMF Callout';

/** True when the paragraph holds a shape with the given name. */
async function hasShapeNamed(ctx: Word.RequestContext, para: Word.Paragraph, name: string): Promise<boolean> {
  try {
    const shapes = para.shapes;
    shapes.load('items/name');
    await ctx.sync();
    return shapes.items.some((s) => s.name === name);
  } catch {
    return false;
  }
}

/** True when both ranges start on the same page (assumes true if pages cannot be read). */
async function startOnSamePage(ctx: Word.RequestContext, a: Word.Range, b: Word.Range): Promise<boolean> {
  try {
    const pagesA = a.pages;
    const pagesB = b.pages;
    pagesA.load('items/index');
    pagesB.load('items/index');
    await ctx.sync();
    const first = pagesA.items[0]?.index;
    const second = pagesB.items[0]?.index;
    return first === undefined || second === undefined || first === second;
  } catch {
    return true;
  }
}


async function insertCalloutBox(profile: TemplateProfile): Promise<void> {
  if (profile.id !== 'template-2') {
    throw new AppError('Callout boxes are only supported in Template-2 (Asset Allocation).', AppErrorCode.ELEMENT_INSERT_FAILED);
  }

  // The floating-shape API requires desktop Word (Microsoft 365) 1.2+.
  if (!Office.context.requirements.isSetSupported('WordApiDesktop', '1.2')) {
    throw new AppError(
      'Callout boxes require Word for Windows/Mac (Microsoft 365). Please update Word and try again.',
      AppErrorCode.ELEMENT_INSERT_FAILED
    );
  }

  await Word.run(async (ctx) => {
    const selection = ctx.document.getSelection();
    selection.paragraphs.load('items');
    const table = selection.parentTableOrNullObject;
    await ctx.sync();

    let anchorPara = selection.paragraphs.items[0];
    let relativeHorizontal: 'Margin' | 'Page' = 'Margin';
    let relativeVertical: 'Paragraph' | 'Margin' = 'Paragraph';
    let left = 0;
    let top = 0;
    let shapeName = CALLOUT_NAME;

    // Word (2013+) always lays out a shape anchored inside a table cell relative
    // to that cell, ignoring the page-margin position. When the cursor is in a
    // table, anchor to a paragraph outside the table instead and place the box in
    // the left page margin, level with the top of the table.
    if (!table.isNullObject) {
      const before = table.getParagraphBeforeOrNullObject();
      before.load('lineSpacing,spaceAfter');
      const after = table.getParagraphAfterOrNullObject();
      await ctx.sync();

      // One callout per exhibit: stop if this table already has one.
      if (
        (!before.isNullObject && (await hasShapeNamed(ctx, before, CALLOUT_NAME_NEXT_TABLE))) ||
        (!after.isNullObject && (await hasShapeNamed(ctx, after, CALLOUT_NAME_PREV_TABLE)))
      ) {
        throw new AppError('This exhibit already has a callout box.', AppErrorCode.ELEMENT_INSERT_FAILED);
      }

      if (!before.isNullObject && (await startOnSamePage(ctx, before.getRange(), table.getRange()))) {
        anchorPara = before;
        shapeName = CALLOUT_NAME_NEXT_TABLE;
        // Measure from the page edge so the box keeps clear space on its left.
        relativeHorizontal = 'Page';
        left = PAGE_LEFT_GAP_PT;
        // Offset past the anchor paragraph (one line + its spacing after) so the
        // box lines up with the table's first row.
        top = before.lineSpacing + before.spaceAfter;
      } else {
        // The table starts a new page, so the paragraph before it is on the
        // previous page (a floating box stays on its anchor's page). Anchor to the
        // paragraph after the table and pin the box to the top margin, where a
        // table that starts a page begins.
        if (!after.isNullObject && (await startOnSamePage(ctx, after.getRange(), table.getRange()))) {
          anchorPara = after;
          shapeName = CALLOUT_NAME_PREV_TABLE;
          relativeHorizontal = 'Page';
          left = PAGE_LEFT_GAP_PT;
          relativeVertical = 'Margin';
          top = 0;
        }
      }
    }

    // Step 1: Create the text box via the JS API (this always adds a default border).
    const shape = anchorPara.insertTextBox(DUMMY_TEXT, {
      width: CALLOUT_WIDTH_PT,
      height: CALLOUT_INIT_HEIGHT_PT,
    });

    // Pin to the left-margin gutter, vertically aligned with this paragraph
    // (or with the table, when inserted from inside one).
    shape.name = shapeName;
    shape.relativeHorizontalPosition = relativeHorizontal;
    shape.left = left;
    shape.relativeVerticalPosition = relativeVertical;
    shape.top = top;
    shape.allowOverlap = false;

    // Body text flows to the RIGHT of the box, never below it.
    shape.textWrap.type = 'Square';
    shape.textWrap.side = 'Right';
    shape.textWrap.leftDistance = 0;
    shape.textWrap.rightDistance = WRAP_GAP_PT;
    shape.textWrap.topDistance = 0;
    shape.textWrap.bottomDistance = 0;

    // Fixed width, small internal padding.
    shape.textFrame.wordWrap = true;
    shape.textFrame.leftMargin = 3;
    shape.textFrame.rightMargin = 3;
    shape.textFrame.topMargin = 2;
    shape.textFrame.bottomMargin = 2;

    // Style the callout text to match the template.
    const body = shape.body;
    body.font.name = profile.typography.fontFamily;
    body.font.size = profile.typography.graphHintSize;
    body.font.color = CALLOUT_TEXT_COLOR;

    body.paragraphs.load('items');
    await ctx.sync();

    const textPara = body.paragraphs.items[0];
    if (textPara) {
      textPara.alignment = 'Left';
      textPara.spaceBefore = 0;
      textPara.spaceAfter = 0;
      textPara.leftIndent = 0;
      textPara.rightIndent = 0;
    }

    await ctx.sync();

    // Step 2: Read the paragraph's OOXML (which now contains the shape with a border).
    const ooxmlResult = anchorPara.getOoxml();
    await ctx.sync();

    let xml = ooxmlResult.value;

    // Step 3: Patch the OOXML to remove the shape border.
    // Replace any existing <a:ln ...>...</a:ln> with <a:ln><a:noFill/></a:ln>
    xml = xml.replace(/<a:ln\b[^>]*>[\s\S]*?<\/a:ln>/g, '<a:ln><a:noFill/></a:ln>');
    // If there's no <a:ln> but there's a </wps:spPr>, inject one before it
    if (!xml.includes('<a:ln')) {
      xml = xml.replace(/<\/wps:spPr>/g, '<a:ln><a:noFill/></a:ln></wps:spPr>');
    }

    // Step 4: Replace the paragraph content with the patched OOXML (borderless).
    anchorPara.insertOoxml(xml, Word.InsertLocation.replace);
    await ctx.sync();
  });
}

/** Element definition for callout box insertion. */
export const calloutBoxElement: ElementDefinition = {
  id: 'callout-box',
  label: 'Callout Box',
  icon: 'TextBoxRegular',
  description: 'Callout box for the left margin',
  category: 'text',
  templateIds: ['template-2'],
  insertAsync: insertCalloutBox,
};
